import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, ChangeEventHandler } from "react";
import { v4 as uuidv4 } from "uuid";
import { Dialog } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { DialogType } from "app/types";
import { uploadImageFile } from "app/api";
import { actions } from "app/configSlice";
import { RootState, AppDispatch } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, usePlaySound, dataURLToBlob } from "common/utils";
import { Spin, CloseBtn, ActionButton, FadeTransition } from "common/elements";
import { sendContact } from "features/universal/api";
import { captureScreen } from "features/universal/captureScreen";

export enum ContactType {
  HOW_TO = "how_to",
  BUG = "bug",
  REQUEST = "request",
  OTHER = "other",
  NONE = "none",
}

const ContactCard = ({
  selected,
  message,
  handleSelect,
  children,
}: {
  selected: boolean;
  message: string;
  handleSelect: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
}) => (
  <button
    className="flex-col-el flex-center relative border-none bg-transparent p-0 m-6 rounded-[12px] overflow-hidden transition"
    onClick={handleSelect}
    style={{
      transform: `translateY(${selected ? 3 : 0}px)`,
      boxShadow: selected
        ? "0px 3px 3px -3px rgba(0, 0, 0, 0.2), 0px 3px 4px 1px rgba(0, 0, 0, 0.14), 0px 1px 8px 3px rgba(0, 0, 0, 0.12)"
        : "0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
    }}
  >
    <div className="flex-col-el flex-center relative">{children}</div>

    <div
      className={`flex-col-center w-[200px] h-[96px] px-4 transition-colors ${
        selected ? "bg-red-100" : "bg-blue-80"
      }`}
    >
      <p className="text-body text-white text-center text-[24px] whitespace-pre">{message}</p>
    </div>

    <div
      className={`flex-col-el flex-center absolute w-full h-full rounded-t-[12px] overflow-hidden box-border transition-colors ${
        selected
          ? "border-solid border-8 border-red-100"
          : "border-solid border-8 border-transparent"
      }`}
    />
  </button>
);

export const Contact = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const DETAIL_MAX_LENGTH = 200;
  const location = useLocation();
  const [detail, setDetail] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(ContactType.NONE);
  // 開くときに渡された画面キャプチャ（data URL）。外すこともできる
  const [capture, setCapture] = useState<string | null>(null);
  const contact = useSelector((state: RootState) => state.config.dialog.contact);
  const activeUser = useSelector((state: RootState) => state.user.active);

  // 端末を振って開いたときは種別と画面キャプチャが渡ってくる。
  // ヘルプボタンなどキャプチャ無しで開いたときは、ここで撮る。
  // captureScreen はダイアログを除いて撮るので、開いたあとでも下の画面が残る
  useEffect(() => {
    if (!contact.value) return;
    const args = contact.args ?? {};
    setType((args.type as ContactType) ?? ContactType.NONE);
    if (args.capture) {
      setCapture(args.capture);
      return;
    }
    let cancelled = false;
    captureScreen().then((image) => {
      if (!cancelled) setCapture(image);
    });
    return () => {
      cancelled = true;
    };
  }, [contact.value, contact.args]);

  const handleClose = () => {
    setType(ContactType.NONE);
    setDetail("");
    setCapture(null);
    dispatch(
      actions.updateDialog({
        type: DialogType.CONTACT,
        value: false,
        args: {},
      }),
    );
  };

  const handleRemoveCapture = () => {
    play();
    setCapture(null);
  };

  const handleSelectType = (contactType: ContactType) => () => {
    play();
    setType(contactType);
  };

  const handleChangeDetail: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    let value: string;
    if (e.target.value.length > DETAIL_MAX_LENGTH) {
      value = e.target.value.substring(0, DETAIL_MAX_LENGTH);
    } else {
      value = e.target.value;
    }
    setDetail(value);
  };

  // 添付は必須ではないので、アップロードに失敗しても本文だけ送る
  const uploadCapture = async () => {
    if (!capture) return undefined;
    try {
      const filename = `users/${activeUser.uid}/contact_capture/${uuidv4()}.jpg`;
      await uploadImageFile({
        fileBlob: dataURLToBlob(capture),
        filename,
        contentType: "image/jpeg",
      });
      return `${Constants.assetHost}/${filename}`;
    } catch (error) {
      console.error("Failed to upload contact capture:", error);
      return undefined;
    }
  };

  const handleSendContact = async () => {
    setLoading(true);
    const image_url = await uploadCapture();

    sendContact({
      uid: activeUser.uid,
      contact: {
        type,
        detail,
        url: `${location.pathname}${location.search}`,
        image_url,
      },
    })
      .then(() => {
        toast.success(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_CONTACT_SEND")}</p>
          </div>,
        );
        setLoading(false);
        handleClose();
      })
      .catch((error) => {
        console.error(JSON.stringify(error));
        setLoading(false);
      });
  };

  return (
    <Dialog
      fullScreen
      maxWidth={false}
      open={contact.value}
      sx={{ zIndex: 100000 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
      transitionDuration={{ enter: 1000, exit: 500 }}
      onClose={handleClose}
    >
      <div
        className="flex-col-center w-full h-full !absolute top-0 left-0 z-50"
        style={{ display: loading ? "flex" : "none" }}
      >
        <Spin />
      </div>
      <div className="flex-col-center !absolute inset-0 bg-textcolor-black/50 w-full h-full">
        <div
          className="flex-col-view items-center justify-start w-[1180px] h-[900px] rounded-[8px] border-[12px] border-solid border-white bg-beige-60"
          style={{ transform: `scale(${scale})`, marginTop: 32 * scale }}
        >
          <CloseBtn close={handleClose} />

          <div className="flex-col-el flex-center w-[568px] mt-8">
            <div className="flex-row-el flex-center relative">
              <p
                className="title-text text-orange-100 text-[46px] tracking-widest text-body-ellipsis px-4"
                style={{ WebkitTextStroke: "16px #FFA03B" }}
              >
                {I18n.t("MSG_CONTACT_TITLE")}
              </p>
              <p
                className="title-text text-body-ellipsis text-[46px] tracking-widest text-white absolute z-10 px-4"
                style={{ WebkitTextStroke: "8px #FEFDFE" }}
              >
                {I18n.t("MSG_CONTACT_TITLE")}
              </p>
              <p className="title-text text-body-ellipsis text-[46px] tracking-widest text-textcolor-black absolute z-50">
                {I18n.t("MSG_CONTACT_TITLE")}
              </p>
            </div>
          </div>

          <div className="flex-col-center mt-16 border-solid border-orange-100 border-4 rounded-[12px] p-6">
            <div className="flex-row-el flex-center bg-beige-60 absolute -top-6 px-2">
              <p className="text-body text-[28px] text-textcolor-black">
                {I18n.t("MSG_CONTACT_TYPE_TITLE")}
              </p>
            </div>

            <div className="flex-row-center">
              <ContactCard
                message={I18n.t("MSG_CONTACT_TYPE_HOW_TO")}
                selected={type === ContactType.HOW_TO}
                handleSelect={handleSelectType(ContactType.HOW_TO)}
              >
                <img
                  alt="お問合せのアイコンー使い方"
                  src={`${Constants.assetHost}/assets/images/contact_how_to.png`}
                  className="w-[200px] h-[200px]"
                />
              </ContactCard>

              <ContactCard
                message={I18n.t("MSG_CONTACT_TYPE_BUG")}
                selected={type === ContactType.BUG}
                handleSelect={handleSelectType(ContactType.BUG)}
              >
                <img
                  alt="お問合せのアイコンーバグ"
                  src={`${Constants.assetHost}/assets/images/contact_bug.png`}
                  className="w-[200px] h-[200px]"
                />
              </ContactCard>

              <ContactCard
                message={I18n.t("MSG_CONTACT_TYPE_REQUEST")}
                selected={type === ContactType.REQUEST}
                handleSelect={handleSelectType(ContactType.REQUEST)}
              >
                <img
                  alt="お問合せのアイコンー機能要望"
                  src={`${Constants.assetHost}/assets/images/contact_request.png`}
                  className="w-[200px] h-[200px]"
                />
              </ContactCard>

              <ContactCard
                message={I18n.t("MSG_CONTACT_TYPE_OTHER")}
                selected={type === ContactType.OTHER}
                handleSelect={handleSelectType(ContactType.OTHER)}
              >
                <img
                  alt="お問合せのアイコンーその他"
                  src={`${Constants.assetHost}/assets/images/contact_other.png`}
                  className="w-[200px] h-[200px]"
                />
              </ContactCard>
            </div>
          </div>

          {/* 添付を左、本文を右に同じ高さで並べる。添付の有無で枠は動かない */}
          <div className="flex-row-el items-start justify-center gap-4 w-full mt-10">
            <div className="flex-col-el flex-center gap-2 w-[220px] h-[160px] box-border p-2 rounded-xl bg-white/60">
              {!capture && (
                <div className="flex-col-el flex-center gap-1">
                  <span className="material-symbols-outlined type-title text-gray2-100">
                    hide_image
                  </span>
                  <p className="type-text type-sm text-gray2-100 leading-tight">
                    {I18n.t("MSG_CONTACT_CAPTURE_EMPTY")}
                  </p>
                </div>
              )}

              {capture && (
                <>
                  <p className="type-text type-sm text-textcolor-black leading-tight whitespace-nowrap">
                    {I18n.t("MSG_CONTACT_CAPTURE_LABEL")}
                  </p>
                  {/* 添付の見た目と外すボタンは ChatInputBar の MessageImagePreview に合わせる */}
                  <div className="flex-col-el relative w-[180px]">
                    <img
                      alt={I18n.t("MSG_CONTACT_CAPTURE_LABEL")}
                      src={capture}
                      className="w-full box-border rounded border-2 border-solid border-gray-20"
                    />
                    <button
                      aria-label={I18n.t("MSG_CONTACT_CAPTURE_REMOVE_BTN")}
                      onClick={handleRemoveCapture}
                      className="type-inherit-font flex-col-el flex-center absolute -top-3 -right-3 w-8 h-8 rounded-full bg-red-80 border-none cursor-pointer"
                    >
                      <span className="material-symbols-outlined type-sm text-white">close</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="flex-col-center w-[640px] h-[160px]">
              <textarea
                readOnly={type === ContactType.NONE}
                className="resize-none input text-body text-textcolor-black text-[26px]"
                placeholder={I18n.t("MSG_CONTACT_DETAIL_TITLE")}
                rows={4}
                minLength={1}
                value={detail}
                onChange={handleChangeDetail}
              />
            </div>
          </div>

          {/* Close Page Button */}
          <div className="flex-row-el flex-center mt-5">
            <ActionButton
              type="primary"
              disabled={type === ContactType.NONE || detail === ""}
              Icon={<MailOutlineIcon sx={{ color: "white", fontSize: "40px" }} />}
              text={I18n.t("MSG_CONTACT_SEND_BTN")}
              onClick={handleSendContact}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};
